import { renderSafeMarkdown } from '../markdown';

export type BriefSource = { id: string; label: string; kind: string; url?: string };
export type BriefSection = { number: string; title: string; label: string; html: string };
export type CallBriefView = { sections: BriefSection[]; cited: (BriefSource & { n: number })[]; uncited: number };

// fund_call_brief_v4's fixed headings (_CALL_BRIEF_SECTIONS in fund_call_brief.py): change one, change the other.
const SHORT: Record<string, string> = {
  'what is this call about?': 'This call',
  'what do the lp and we want from each other?': 'What each side wants',
  'summary of recent threads': 'Recent threads',
  'key updates to provide the lp': 'Updates to give',
  'key questions to deepen the relationship and explore additional investment': 'Questions',
};
const SECTION = /^##\s+(\d+)\.\s+(.+?)\s*$/gm;
const LP_APPENDIX = /\n\s*---\s*\n\s*#\s+Full LP relationship brief\b[\s\S]*$/i;
const LP_CITATION = /\s*\[lp-[a-z][a-z0-9:._-]*\]/gi;  // storage keys, never shown
const CITATION = /\s*\[([a-z]\d+)\]/g;  // m1, n2, r1, w1, f1 — the ids the brief prompt cites
const escape = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The call-specific sections with each cited source id turned into a number, in reading order. */
export function callBriefView(content: string, sources: BriefSource[]): CallBriefView {
  const text = (content || '').replace(LP_APPENDIX, '').replace(LP_CITATION, '').trim();
  const byId = new Map(sources.map(source => [source.id, source]));
  const order: string[] = [];
  // Rendered first, then numbered: the marker is our own escaped HTML, outside the sanitiser.
  const html = (markdown: string) => renderSafeMarkdown(markdown).replace(CITATION, (match, id: string) => {
    const source = byId.get(id);
    if (!source) return match;  // not a citation: leave ordinary bracketed text alone
    if (!order.includes(id)) order.push(id);
    return `<sup class="cal-ref" title="${escape(source.label)}">${order.indexOf(id) + 1}</sup>`;
  });
  const found = [...text.matchAll(SECTION)];
  const sections: BriefSection[] = [];
  const lead = found.length ? text.slice(0, found[0].index).trim() : text;
  if (lead) sections.push({ number: '', title: '', label: '', html: html(lead) });
  found.forEach((match, i) => {
    const body = text.slice(match.index! + match[0].length, found[i + 1]?.index ?? text.length).trim();
    sections.push({ number: match[1], title: match[2], label: SHORT[match[2].toLowerCase()] || match[2], html: html(body) });
  });
  return {
    sections,
    cited: order.map((id, i) => ({ ...byId.get(id)!, n: i + 1 })),
    uncited: sources.filter(source => !order.includes(source.id)).length,
  };
}

/** Only an https link is shown as a link. */
export const safeUrl = (url?: string | null) => (url && /^https:\/\//.test(url) ? url : '');

export const providerName = (url: string) => {
  try { return new URL(url).hostname.endsWith('google.com') ? 'Google Calendar' : 'Outlook'; }
  catch { return 'your calendar'; }
};
